import { ApolloError, DocumentNode, useQuery } from '@apollo/client'
import { useState } from 'react'

/** The shape every cursor-paginated ticketing response shares. */
export interface CursorPage<TItem> {
  items: TItem[]
  nextPageToken?: string | null
}

export interface UseCursorPageArgs<TData, TItem> {
  query: DocumentNode
  /** Variables other than pageSize/pageToken, which this hook owns. */
  variables?: Record<string, unknown>
  /**
   * Page size, or `null` for a resource that is NOT paginated — the hook then
   * sends neither pageSize nor pageToken and never reports more to load.
   *
   * `null` rather than "omit the prop", because omitting it means "use the
   * default 20", and that default is what broke the notification Health screen:
   * the hook owns pageSize and injects it unconditionally, while
   * GetChannelHealth rejects any non-zero page size outright with
   * "channel health does not support pagination". Every request failed.
   */
  pageSize?: number | null
  /**
   * Upper bound on retained items. Omit for lists with a natural ceiling (one
   * tenant's tickets, one ticket's thread); set it where there is none.
   */
  maxRetained?: number
  /** Pluck the paginated field out of the response. */
  getPage: (data?: TData) => CursorPage<TItem> | undefined
  /** Rebuild the response with a different item list, preserving its shape. */
  withItems: (data: TData, items: TItem[]) => TData
  /** Return a safe, user-visible error for a malformed backend response. */
  validate?: (data: unknown) => string | undefined
  /**
   * Hold the query until a required variable exists. Additive and defaulted, so
   * the ticketing callers are unaffected: a list whose identity comes from a
   * prop (one user's preference history) must not fire a request with that
   * variable undefined, which the backend would answer for the wrong subject or
   * reject.
   */
  skip?: boolean
}

export interface UseCursorPageResult<TItem> {
  items: TItem[]
  loading: boolean
  error?: ApolloError
  hasMore: boolean
  loadMore: () => void
  validationError?: string
  /**
   * Re-runs the first page. Exposed so a failed list can offer Retry — the
   * alternative was a full page reload, which on the tabbed support screen
   * also throws away the tab the operator was on.
   */
  refetch: () => void
}

/**
 * Cursor pagination for the ticketing lists.
 *
 * WHY THIS EXISTS AS A HOOK rather than three copies of the same twenty lines:
 * the `updateQuery` inside is not incidental similarity, it is a WORKAROUND for
 * a property of the shared Apollo cache. This app builds `InMemoryCache` with
 * no `typePolicies`, so Apollo's default field write REPLACES a paginated list
 * with the incoming page. Every list therefore has to merge by hand.
 *
 * That makes the duplication dangerous rather than merely repetitive: the day
 * someone adds a `typePolicies` merge function for one of these fields, the
 * hand-merge for that field starts double-counting, and only the call sites
 * updated in lockstep stay correct. One implementation is one place to change.
 *
 * TWO RULES THIS ENCODES, both of which a naive list gets wrong:
 *
 *   1. `hasMore` comes from the TOKEN, never from item count. The comments
 *      endpoint filters a page after reading it, so 20 stored rows can arrive
 *      as 15 — or 0 — with more behind them. A caller that stopped on a short
 *      page would truncate the thread precisely where comments were withheld.
 *
 *   2. `loadMore` swallows nothing. `fetchMore` rejects independently of
 *      `useQuery`'s `error`, so an unhandled rejection would leave the button
 *      idle with no signal. The rejection surfaces through `error`; the catch
 *      only stops it becoming an unhandled promise rejection.
 */
/**
 * The default page size, exported because callers have to be able to name it.
 *
 * A caller that writes into the cache by hand must reproduce this hook's query
 * variables EXACTLY — Apollo keys cached results by them, so a mismatch is not
 * an error, it is a silent miss that leaves the write landing nowhere. The
 * ticket screen's comment-append did that with a literal `20` matching this
 * default two files away, in a hook the component it was reaching into does not
 * even pass a value to. Changing this line would have broken that append with
 * nothing failing anywhere.
 */
export const DEFAULT_PAGE_SIZE = 20

export function useCursorPage<TData, TItem>({
  query,
  variables,
  pageSize = DEFAULT_PAGE_SIZE,
  maxRetained,
  getPage,
  withItems,
  validate,
  skip
}: UseCursorPageArgs<TData, TItem>): UseCursorPageResult<TItem> {
  const [fetchValidationError, setFetchValidationError] = useState<string>()
  const paginated = pageSize !== null
  const { data, loading, error, fetchMore, refetch } = useQuery<TData>(query, {
    variables: paginated ? { ...variables, pageSize } : { ...variables },
    // These lists are stale the moment another agent touches them.
    fetchPolicy: 'cache-and-network',
    // Without this, `loading` reflects only the FIRST fetch: a fetchMore never
    // flips it, so a spinner never appears and a disabled-while-loading button
    // never disables — two quick clicks then both fire with the same captured
    // token and append the same page twice, under duplicate React keys.
    notifyOnNetworkStatusChange: true,
    skip
  })

  const validationError =
    (data ? validate?.(data) : undefined) || fetchValidationError
  const page = validationError ? undefined : getPage(data)

  function loadMore(): void {
    // A non-paginated resource has no next page to ask for, and asking would
    // send the pageToken its RPC rejects.
    if (!paginated) return
    fetchMore({
      variables: { ...variables, pageSize, pageToken: page?.nextPageToken },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev
        const fetchError = validate?.(fetchMoreResult)
        if (fetchError) {
          setFetchValidationError(fetchError)
          return prev
        }
        setFetchValidationError(undefined)
        const merged = [
          ...(getPage(prev)?.items || []),
          ...(getPage(fetchMoreResult)?.items || [])
        ]
        return withItems(
          fetchMoreResult,
          maxRetained ? merged.slice(-maxRetained) : merged
        )
      }
    }).catch(() => {
      // Reported through `error`; this only prevents an unhandled rejection.
    })
  }

  return {
    items: page?.items || [],
    loading,
    error,
    hasMore: paginated && Boolean(page?.nextPageToken),
    loadMore,
    validationError,
    // Exposed so a failed list can offer Retry. Without it the only recovery
    // is a full page reload, which on a tabbed screen also loses the tab.
    refetch
  }
}
